import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  TrendingUp,
  Building2,
  Users,
  Shield,
  DollarSign,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Wrench,
  ShoppingCart,
  Baby,
  Store,
  PiggyBank,
  Calendar,
  Scale,
  FileText,
  AlertTriangle,
  Key,
} from "lucide-react";

/* ── FAQ Item ── */
const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-bold text-slate-900 pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#002B7F] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const deductions = [
  { icon: Wrench, title: "Repairs & Maintenance", desc: "Necessary upkeep, appliance replacement, structural repairs" },
  { icon: Shield, title: "Insurance", desc: "Property insurance and liability coverage" },
  { icon: FileText, title: "Management Costs", desc: "Reasonable property management fees" },
  { icon: Building2, title: "Capital Improvements", desc: "Verified upgrades that improve the property" },
];

const communityImpact = [
  { icon: ShoppingCart, title: "Groceries", desc: "Families spend more on food and essentials locally" },
  { icon: Baby, title: "Childcare", desc: "More families can afford quality childcare" },
  { icon: Store, title: "Local Businesses", desc: "More disposable income flows into Nevada businesses" },
  { icon: PiggyBank, title: "Savings", desc: "Families can actually save — for emergencies, education, homeownership" },
];

const faqs = [
  {
    question: "Will this reduce the supply of rental housing?",
    answer: "This plan is designed to reduce excessive pricing, not eliminate rental housing. Responsible landlords earning reasonable returns are unaffected. Some speculative investors may exit, but their properties don't disappear — they become purchase opportunities for families or are acquired by responsible operators. The net effect is a healthier, more balanced rental market.",
  },
  {
    question: "Will landlords leave the market?",
    answer: "Some speculative, high-margin investors may reduce their footprint — and that's by design. But landlords earning fair returns, maintaining their properties, and operating responsibly will continue to do well. The plan specifically protects legitimate operating expenses through deductions. The goal is to remove the incentive for excessive pricing, not to eliminate landlords.",
  },
  {
    question: "Why 7x local property taxes as the threshold?",
    answer: "Using 7x local property taxes as the threshold ties rental income to a stable, transparent benchmark that landlords and renters can both understand. Because property taxes in Nevada are capped at a nominal increase each year, the rental threshold moves slowly and predictably — meaning rents won't change drastically year to year. This gives landlords a healthy, fair return while preventing the kind of extreme rent spikes that price families out.",
  },
  {
    question: "How are legitimate costs handled?",
    answer: "Landlords can deduct verified expenses — repairs, maintenance, insurance, and reasonable management costs — before the threshold calculation. This means a landlord who invests in their property is rewarded, not penalized. Only verified expenses qualify, with anti-fraud enforcement to prevent abuse.",
  },
  {
    question: "What prevents loopholes?",
    answer: "The system applies to all rental income — including short-term rentals like Airbnb and weekly rentals. Properties cannot be reclassified to avoid the system. Common ownership structures like LLCs are tracked and aggregated. The goal is simple: if you're collecting rent in Nevada, the same fair rules apply.",
  },
  {
    question: "Isn't this just rent control?",
    answer: "Traditional rent control caps what landlords can charge. This plan is different — it doesn't cap rent. Instead, it makes excessive rent increases economically inefficient by taxing the excess margin. Landlords can still charge whatever they want, but the incentive to dramatically overcharge is removed. This market-based approach is more effective and more sustainable than traditional rent control.",
  },
];

const RentalPage = () => {
  return (
    <div data-testid="rental-page" className="pt-16 md:pt-20">
      {/* ── HERO ── */}
      <section className="relative py-16 md:py-24 bg-slate-900">
        <div className="absolute inset-0 bg-[#002B7F]/90" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
            Lowering Rental Costs
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-4 max-w-3xl">
            Bringing Rent Back Within Reach for Nevada Families
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mb-8">
            A structured plan to reduce excessive rent increases, stabilize
            housing costs, and keep billions of dollars in Nevada communities
            every year.
          </p>

          <div className="flex flex-wrap gap-4">
            <a
              href="#the-plan"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Read the Full Plan
            </a>
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Get Involved
            </Link>
          </div>

          {/* Video Overview */}
          <div className="mt-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Watch the Overview</p>
            <div className="relative aspect-video bg-black border border-white/20 overflow-hidden">
              <iframe
                src="https://www.youtube.com/embed/I4BxM5-NcR0?rel=0&modestbranding=1"
                title="Lowering Rental Costs - Cooper for Nevada"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                data-testid="rental-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── THE PROBLEM ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                Why Rent Is Rising
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Rent across Nevada is increasing faster than wages. Housing is
                increasingly treated as a high-yield investment, and investor
                ownership pressure is pricing families out of stability.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  {
                    icon: TrendingUp,
                    title: "Rent Outpacing Wages",
                    desc: "Rental costs rising significantly faster than what Nevada families earn",
                  },
                  {
                    icon: DollarSign,
                    title: "Housing as High-Yield Investment",
                    desc: "Properties acquired specifically to maximize rental income, not serve communities",
                  },
                  {
                    icon: Building2,
                    title: "Investor Ownership Pressure",
                    desc: "Large portfolios driving up rents across entire neighborhoods",
                  },
                  {
                    icon: Users,
                    title: "Families Priced Out",
                    desc: "Working families forced to move, double up, or spend unsustainable portions of income on rent",
                  },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="flex items-start gap-3 p-4 bg-red-50 border border-red-200"
                    >
                      <Icon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-red-800 text-sm">{item.title}</p>
                        <p className="text-red-700 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Key stat */}
            <div>
              <div className="bg-slate-900 p-8 text-center mb-6">
                <p className="text-5xl md:text-6xl font-black text-[#D4AF37] mb-2">
                  ~$10B
                </p>
                <p className="text-white/80">
                  Annual rent paid by Nevada renters
                </p>
              </div>
              <div className="bg-[#002B7F] p-6">
                <p className="text-white font-bold">
                  That's $10 billion every year — much of it leaving Nevada
                  communities and going to out-of-state investors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CORE IDEA ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            What This Plan Does
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-8">
            This plan removes the incentive to dramatically increase rent beyond
            reasonable levels. It doesn't cap rent — it makes excessive rent
            increases economically inefficient, so the market naturally adjusts
            toward fairer pricing.
          </p>
          <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-6">
            <p className="text-[#002B7F] font-bold text-lg">
              A market-based approach that discourages excessive pricing without
              traditional rent control.
            </p>
          </div>
        </div>
      </section>

      {/* ── RENTAL INCOME THRESHOLD ── */}
      <section id="the-plan" className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              The Rental Income Threshold System
            </h2>
          </div>

          {/* Step by step */}
          <div className="space-y-6 mb-12">
            {/* Step 1 */}
            <div className="bg-[#F8F9FA] border border-slate-200 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#002B7F] text-white font-black text-lg">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    Set a Reasonable Baseline
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-3">
                    Rental income is tied to approximately 7x the property's
                    local property taxes annually. Because Nevada property taxes
                    are capped at a nominal increase each year, this threshold
                    moves slowly and predictably — meaning rents won't change
                    drastically year to year.
                  </p>
                  <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-4 py-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-800 font-bold text-sm">
                      7x property taxes = a stable, fair return for property
                      owners
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 — Example */}
            <div className="bg-[#F8F9FA] border border-slate-200 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#D4AF37] text-black font-black text-lg">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    Real-World Example
                  </h3>
                  <div className="bg-white border border-slate-200 p-5 mb-3">
                    <div className="grid sm:grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-xs text-slate-500 font-bold uppercase mb-1">
                          Annual Property Tax
                        </p>
                        <p className="text-2xl font-black text-slate-900">
                          $3,000
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 font-bold uppercase mb-1">
                          Annual Threshold (7x)
                        </p>
                        <p className="text-2xl font-black text-[#002B7F]">
                          $21,000
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 font-bold uppercase mb-1">
                          Monthly Baseline
                        </p>
                        <p className="text-2xl font-black text-emerald-600">
                          ~$1,750
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-slate-600 text-sm">
                    A property with $3,000/year in property taxes generates a
                    healthy ~$1,750/month baseline rent under this system.
                    Because property taxes are capped at nominal annual
                    increases, this threshold — and rents — stay stable and
                    predictable year to year. That's a strong return for
                    any property owner.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-[#F8F9FA] border border-slate-200 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-emerald-600 text-white font-black text-lg">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    Predictable & Transparent
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    This creates a predictable, transparent baseline for rental
                    pricing. Both landlords and renters know exactly where the
                    threshold sits. No surprises, no arbitrary increases, no
                    hidden calculations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── EXCESS RENT TAX ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            The Excess Rent Structure
          </h2>
          <p className="text-white/80 leading-relaxed mb-6">
            Rental income above the 7x property tax threshold is taxed at a
            ~70% marginal rate. This doesn't cap what a landlord can charge —
            it makes charging dramatically above fair market value economically
            inefficient.
          </p>

          <div className="bg-white/10 border border-white/20 p-6 mb-8">
            <p className="text-white font-bold text-lg">
              Excess rent becomes economically inefficient, discouraging price
              spikes.
            </p>
          </div>

          <div className="space-y-4">
            <div className="bg-white/10 border border-white/20 p-5">
              <p className="text-white font-bold text-sm mb-2">
                Below the threshold
              </p>
              <p className="text-white/70 text-sm">
                Full rental income retained. No additional tax. Healthy returns
                for landlords.
              </p>
            </div>
            <div className="bg-white/10 border border-red-400/40 p-5">
              <p className="text-white font-bold text-sm mb-2">
                Above the threshold
              </p>
              <p className="text-white/70 text-sm">
                ~70% marginal tax on the excess. For every additional dollar
                charged above the threshold, only ~30 cents is retained. The
                incentive to overcharge is removed.
              </p>
            </div>
          </div>

          <p className="text-[#D4AF37] font-bold mt-8">
            The goal is not punishment — it is removing the excessive profit
            incentive that drives rent spikes.
          </p>
        </div>
      </section>

      {/* ── WHY THIS WORKS ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center">
            Why This Works
          </h2>

          <div className="space-y-6">
            {[
              {
                title: "Investors respond to incentives",
                desc: "When the incentive to charge dramatically above fair value is removed, pricing naturally adjusts downward. This is basic market economics.",
              },
              {
                title: "High rent spikes are driven by profit margins",
                desc: "Landlords don't raise rent because costs increased — they raise rent because they can. This system makes that strategy unprofitable.",
              },
              {
                title: "Reducing margin reduces incentive to overcharge",
                desc: "When 70% of excess rental income goes to tax, there's simply no business case for dramatic rent increases. The market self-corrects.",
              },
            ].map((item, i) => (
              <div key={item.title} className="flex items-start gap-4 p-5 bg-[#F8F9FA] border border-slate-200">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#002B7F] text-white font-bold">
                  {i + 1}
                </div>
                <div>
                  <p className="font-bold text-slate-900 mb-1">{item.title}</p>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LANDLORD PROTECTIONS ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                Responsible Landlord Protection
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Landlords who maintain their properties and operate responsibly
                are fully protected. Verified expenses are deducted before the
                threshold calculation — so investment in the property is
                rewarded, not penalized.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {deductions.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="flex items-start gap-3 p-4 bg-white border border-slate-200"
                    >
                      <Icon className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-slate-900 text-sm">{item.title}</p>
                        <p className="text-slate-600 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="bg-white border border-slate-200 p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-700 text-sm">
                    <strong>Rules:</strong> Only verified expenses qualify.
                    Anti-fraud enforcement ensures the system isn't abused.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#002B7F] p-8">
              <p className="text-white/90 leading-relaxed mb-4 text-lg">
                Responsible landlords are protected.
              </p>
              <p className="text-[#D4AF37] font-bold text-xl">
                Excessive pricing is not.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SHORT-TERM RENTALS ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Key className="w-10 h-10 text-[#002B7F] mx-auto mb-6" />
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            Short-Term Rentals Included
          </h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            Airbnb, VRBO, weekly rentals, and all other short-term rental
            platforms are included in this system. The same rules apply. No
            loopholes, no reclassification tricks, no exceptions.
          </p>
          <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-6">
            <p className="text-[#002B7F] font-bold">
              Homes should serve residents first, not speculative short-term
              profit systems.
            </p>
          </div>
        </div>
      </section>

      {/* ── ECONOMIC IMPACT ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Billions Staying in Nevada
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              With approximately $10 billion in annual rent, even a 30-40%
              reduction means $3-4 billion per year stays in Nevada families and
              communities instead of leaving the state.
            </p>
          </div>

          {/* Big numbers */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/10 border border-white/20 p-8 text-center">
              <p className="text-4xl md:text-5xl font-black text-[#D4AF37] mb-2">
                ~$10B
              </p>
              <p className="text-white/70 text-sm">Annual rent in Nevada</p>
            </div>
            <div className="bg-white/10 border border-white/20 p-8 text-center">
              <p className="text-4xl md:text-5xl font-black text-[#D4AF37] mb-2">
                30-40%
              </p>
              <p className="text-white/70 text-sm">Projected reduction</p>
            </div>
            <div className="bg-emerald-600/80 border border-emerald-400/40 p-8 text-center">
              <p className="text-4xl md:text-5xl font-black text-white mb-2">
                $3-4B
              </p>
              <p className="text-white/80 text-sm">
                Staying in Nevada communities annually
              </p>
            </div>
          </div>

          {/* Where it goes */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {communityImpact.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-white/10 border border-white/20 p-5">
                  <Icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                  <h3 className="font-bold text-white text-sm mb-1">
                    {item.title}
                  </h3>
                  <p className="text-white/70 text-xs">{item.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 bg-white/10 border border-white/20 p-5 text-center max-w-2xl mx-auto">
            <p className="text-[#D4AF37] font-bold">
              This is money staying in Nevada families and communities instead
              of leaving the state.
            </p>
          </div>
        </div>
      </section>

      {/* ── MARKET TRANSITION ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                Market Transition — Controlled & Gradual
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Some investor exits are expected — and that's by design.
                Properties they sell become homeownership opportunities for
                families. The transition is controlled and gradual, not
                disruptive.
              </p>
              <div className="space-y-3">
                {[
                  "Some investor exits create purchase opportunities for families",
                  "Properties transition from rental portfolios to ownership",
                  "Controlled, gradual adjustment — not a market shock",
                  "Remaining rentals stabilize at fair pricing levels",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#002B7F] p-8">
              <p className="text-white/90 leading-relaxed mb-4">
                This plan is designed to transition the market — not destabilize
                it.
              </p>
              <p className="text-[#D4AF37] font-bold text-lg">
                Stability for renters. Opportunity for buyers. Fairness for
                everyone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── RENTAL MARKET BALANCE ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Scale className="w-10 h-10 text-[#002B7F] mx-auto mb-6" />
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            Rental Market Balance
          </h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            Nevada still needs rentals. Renting supports mobility, flexibility,
            and choice. This plan doesn't eliminate the rental market — it
            restores balance between renters, property owners, and the broader
            housing market.
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { title: "Renters", desc: "Stable, fair pricing. No sudden spikes" },
              { title: "Landlords", desc: "Fair returns. Protected expenses. Predictable system" },
              { title: "Community", desc: "Billions stay local. Stronger neighborhoods" },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white border border-slate-200 p-5"
              >
                <p className="font-bold text-[#002B7F] mb-1">{item.title}</p>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL SUMMARY + CTA ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xl md:text-2xl font-bold text-slate-900 mb-8">
            This plan restores balance between renters, owners, and the housing
            market itself.
          </p>
          <p className="text-slate-600 leading-relaxed mb-8">
            Families deserve stable, fair housing costs. Landlords deserve fair
            returns. And Nevada communities deserve to keep the money that flows
            through them.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Fair Rent for Nevada Families
          </h2>
          <p className="text-white/70 mb-8">
            Join the movement to restore rental fairness in Nevada.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Join the Movement
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Contact the Campaign
            </Link>
            <Link
              to="/policies/clean-energy"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white/80 hover:border-white hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
            >
              Clean Energy & Jobs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RentalPage;
